import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { extractErrorMessage } from '../../core/services/api';
import {
  llmAgent,
  resources,
  type LlmJobState,
  type LlmJobStatus,
} from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import type { LineOfBizCategory } from '../../core/types/data-models';

/**
 * Shape of `result` returned by `generateCategoryAction`. Mirrors the
 * "Output format" stanza in `generate-category.prompt.md` — a list of
 * categories, each with a description and a list of subcategories.
 */
interface CategorySuggestion {
  category: string;
  categoryDescription?: string;
  /** Relative importance 0–100 (maps to LineOfBizCategory.promptsWeight). */
  weight?: number;
  subcategories?: Array<{
    subcategory: string;
    subcategoryDescription?: string;
  }>;
}

interface CategoriesResult {
  categories?: CategorySuggestion[];
}

interface GenerateCategoriesDialogProps {
  open: boolean;
  lineOfBizId: number | null;
  onClose: () => void;
}

const TERMINAL_STATES: ReadonlySet<LlmJobState> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

/**
 * Dialog that drives the generate-category flow:
 *
 *   1. POST /line-of-biz-categories/generate-category/:lineOfBizId to
 *      start a job.
 *   2. Poll GET /llm-agent/jobs/:jobId every 1.5s until terminal.
 *   3. Render the result with one checkbox per suggested category; the
 *      user picks which to save and clicks Save.
 *   4. For each picked category, create one LineOfBizCategory row with
 *      `subcategories` rendered as a Markdown bullet list — that field
 *      is plain text in the data model, not a relation.
 */
export function GenerateCategoriesDialog({
  open,
  lineOfBizId,
  onClose,
}: GenerateCategoriesDialogProps) {
  const { activeTenantId } = useTenant();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<LlmJobStatus<CategoriesResult> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const jobIdRef = useRef<string | null>(null);

  // ----- start job ----------------------------------------------------
  const start = useMutation({
    mutationFn: (id: number) => resources.generateLineOfBizCategory(id),
    onSuccess: (data) => {
      jobIdRef.current = data.jobId;
      setStatus(data.status as LlmJobStatus<CategoriesResult>);
    },
    onError: (err) => setStartError(extractErrorMessage(err)),
  });

  // ----- save selection ----------------------------------------------
  // Each picked category becomes a fresh LineOfBizCategory row.
  // `subcategories` is plain Markdown text on the data model, so we
  // render the picked sub-list as a `- name — description` bullet list.
  // We don't try to dedupe against existing categories — the user is
  // reviewing the list and can drop suggestions they already have.
  const save = useMutation({
    mutationFn: async (picks: CategorySuggestion[]) => {
      if (lineOfBizId == null) return;
      for (const pick of picks) {
        const name = pick.category.trim();
        if (!name) continue;
        await resources.lineOfBizCategories.create({
          lineOfBizId,
          name,
          description: pick.categoryDescription?.trim() || null,
          subcategories: renderSubcategoriesMarkdown(pick.subcategories),
          commonCategoryId: null,
          promptsWeight: clampWeight(pick.weight),
          ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
        } as Partial<LineOfBizCategory>);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-categories'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const retry = () => {
    if (lineOfBizId == null) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    start.mutate(lineOfBizId);
  };

  // ----- open / reset / start -----------------------------------------
  useEffect(() => {
    if (!open) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    if (lineOfBizId != null) start.mutate(lineOfBizId);
    // start.mutate identity is stable for the mutation hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lineOfBizId]);

  // ----- polling loop -------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (status == null) return;
    if (TERMINAL_STATES.has(status.state)) return;
    let cancelled = false;
    const tick = async () => {
      const jobId = jobIdRef.current;
      if (!jobId) return;
      try {
        const next = await llmAgent.getJob<CategoriesResult>(jobId);
        if (!cancelled) setStatus(next);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    };
    const handle = window.setInterval(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, status]);

  // Default selection — once a job completes with categories, preselect
  // them all so Save is one click for the common "looks good" case.
  useEffect(() => {
    if (status?.state !== 'completed') return;
    const cats = status.result?.categories ?? [];
    setSelected(new Set(cats.map((_, idx) => idx)));
  }, [status]);

  const categories = status?.result?.categories ?? [];
  const lastStep = status?.steps?.[status.steps.length - 1]?.step;

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const isCompleted = status?.state === 'completed';
  const isRunning =
    start.isPending || (status != null && !TERMINAL_STATES.has(status.state));
  const canSave = isCompleted && selected.size > 0 && !save.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setError(null);
    const picks = categories.filter((_, idx) => selected.has(idx));
    await save.mutateAsync(picks);
  };

  return (
    <FormDialog
      open={open}
      title="Generate categories"
      submitLabel={save.isPending ? 'Saving…' : 'Save selected'}
      loading={save.isPending}
      submitDisabled={!canSave}
      maxWidth="lg"
      onSubmit={handleSubmit}
      onClose={() => {
        if (save.isPending) return;
        onClose();
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {/* ---- Job progress ---- */}
      {isRunning && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {status?.state === 'pending' || status == null
                ? 'Starting…'
                : lastStep ?? 'Researching buyer categories…'}
            </Typography>
          </Stack>
          <LinearProgress
            variant={
              status?.progress != null && status.progress > 0
                ? 'determinate'
                : 'indeterminate'
            }
            value={
              status?.progress != null
                ? Math.round(status.progress * 100)
                : undefined
            }
          />
        </Stack>
      )}

      {status?.state === 'failed' && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {status.error || 'The category-research job failed.'}
        </Alert>
      )}
      {status?.state === 'cancelled' && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          The category-research job was cancelled.
        </Alert>
      )}
      {startError && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {startError}
        </Alert>
      )}

      {/* ---- Results review ---- */}
      {isCompleted && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {categories.length === 0 ? (
            <Alert severity="warning">
              The analyzer returned no categories.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {categories.map((c, idx) => (
                <Paper
                  key={`${c.category}-${idx}`}
                  variant="outlined"
                  sx={{ p: 1.5 }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Checkbox
                      checked={selected.has(idx)}
                      onChange={() => toggle(idx)}
                      sx={{ p: 0.5, mt: -0.25 }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, minWidth: 0 }}>
                          {c.category}
                        </Typography>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`Weight ${clampWeight(c.weight)}%`}
                        />
                      </Stack>
                      {c.categoryDescription && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {c.categoryDescription}
                        </Typography>
                      )}

                      {/* Subcategories — preview the Markdown that
                          will be written into `subcategories`. We show
                          a bulleted list (not the raw Markdown source)
                          for legibility; the actual save renders the
                          same content as `- name — description`
                          bullets. */}
                      {(c.subcategories?.length ?? 0) > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Subcategories
                          </Typography>
                          <Stack
                            component="ul"
                            spacing={0.25}
                            sx={{ pl: 2.5, my: 0.5 }}
                          >
                            {c.subcategories?.map((s, sIdx) => (
                              <Typography
                                key={`${s.subcategory}-${sIdx}`}
                                component="li"
                                variant="body2"
                              >
                                <strong>{s.subcategory}</strong>
                                {s.subcategoryDescription
                                  ? ` — ${s.subcategoryDescription}`
                                  : ''}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </FormDialog>
  );
}

/**
 * Render the model's `subcategories` array as a Markdown bullet list so
 * it can be written into `LineOfBizCategory.subcategories` (plain text
 * column). Empty array → `null` so we don't store an empty string. The
 * `—` em-dash matches the preview shown in the dialog.
 */
/** Coerce the model's weight into a valid 0–100 integer (0 when missing). */
function clampWeight(w: number | undefined): number {
  if (typeof w !== 'number' || !Number.isFinite(w)) return 0;
  return Math.max(0, Math.min(100, Math.round(w)));
}

function renderSubcategoriesMarkdown(
  subs: CategorySuggestion['subcategories'],
): string | null {
  if (!subs || subs.length === 0) return null;
  const lines = subs
    .map((s) => {
      const name = s.subcategory?.trim();
      if (!name) return null;
      const desc = s.subcategoryDescription?.trim();
      return desc ? `- ${name} — ${desc}` : `- ${name}`;
    })
    .filter((s): s is string => Boolean(s));
  return lines.length ? lines.join('\n') : null;
}
